import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

import colors from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";

import {
  useAlanCredentials,
  useDeleteAlanCredential,
  useSetDefaultAlanCredential,
} from "../../Query/useAlanCredentials";

const AlanCredentialsAllPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("credentials");
  const { colors: workspace } = useWorkspaceColors();

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedCredentialId, setSelectedCredentialId] = useState<
    number | null
  >(null);

  const { data: alanCredentials, isLoading, isError } = useAlanCredentials();
  const { mutate: deleteCredential } = useDeleteAlanCredential();
  const { mutate: setDefaultCredential } = useSetDefaultAlanCredential();

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedCredentialId) {
      deleteCredential(selectedCredentialId);
    }
  };

  const handleSetDefault = (id: number) => {
    setDefaultCredential(id);
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: colors.red700 }}>
        Error: Failed to fetch Alan Credentials list
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 600,
    color: colors.gray700,
    backgroundColor: colors.gray50,
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: "0.8rem",
  };

  const bodyCellSx = {
    color: colors.primary_dark,
    fontSize: "0.875rem",
    borderBottom: `1px solid ${colors.gray100}`,
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <VpnKeyOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("alanCredentials")}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray500 }}>
                  {alanCredentials?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/alan_credentials/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNewAlanCredential")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "70%" }}>
                    {t("name")}
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "30%" }}
                    align="right"
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(alanCredentials ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{
                        color: colors.gray500,
                        py: 6,
                        backgroundColor: colors.gray50,
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("noCredentials")}
                    </TableCell>
                  </TableRow>
                ) : (
                  (alanCredentials ?? []).map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        transition: "background-color 0.15s ease-in-out",
                        "&:hover": {
                          backgroundColor: `${workspace.primary_dark}08`,
                        },
                      }}
                    >
                      <TableCell sx={bodyCellSx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{item.name}</span>
                          {item.default && (
                            <Chip
                              label="Default"
                              size="small"
                              sx={{
                                bgcolor: colors.orange50,
                                color: colors.orange600,
                                border: `1px solid ${colors.orange200}`,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        <Tooltip title={t("setDefault")}>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefault(item.id)}
                            sx={{
                              color: item.default
                                ? workspace.accent
                                : colors.gray400,
                            }}
                          >
                            {item.default ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCredentialId(item.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{ color: colors.red600 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        title={t("deleteAlanCredentialTitle")}
        description={t("deleteAlanCredentialDescription")}
        content={t("deleteAlanCredentialContent")}
        actions={[
          {
            label: t("no"),
            onClick: () => handleConfirmationDialogClose(false),
          },
          {
            label: t("yes"),
            onClick: () => handleConfirmationDialogClose(true),
          },
        ]}
      />
    </Box>
  );
};

export default AlanCredentialsAllPage;
